import React, { useState } from 'react';
import { ArrowLeftIcon } from '@heroicons/react/outline';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import { PatientCreateFormProps, CreatePatientFormData } from '../types/patient-management.types';
import CommonAnimatedSpinner from '../../common/animated/spinner';
import StyledLabel from '../../common/form/styled-label';

function PatientCreateForm({ onCancel, onPatientCreated, isLoading }: PatientCreateFormProps) {
  const [formData, setFormData] = useState<CreatePatientFormData>({
    name: '',
    lastName: '',
    birthDate: '',
    personCode: '',
    genderId: 1,
  });

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const [errors, setErrors] = useState<Partial<Record<keyof CreatePatientFormData, string>>>({});

  const handleInputChange =
    (field: keyof CreatePatientFormData) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const value = field === 'genderId' ? Number(e.target.value) : e.target.value;

      setFormData((prev) => ({ ...prev, [field]: value }));

      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    };

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
    const dateString = date ? date.toISOString().split('T')[0] : '';

    setFormData((prev) => ({ ...prev, birthDate: dateString }));

    if (errors.birthDate) {
      setErrors((prev) => ({ ...prev, birthDate: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof CreatePatientFormData, string>> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (formData.birthDate) {
      const birthDate = new Date(formData.birthDate);
      const today = new Date();

      if (birthDate > today) {
        newErrors.birthDate = 'Birth date cannot be in the future';
      }
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await onPatientCreated(formData);
    } catch (error) {
      // Error handling for patient creation failure
    }
  };

  const calculateAge = (birthDate?: string): number => {
    if (!birthDate) return 0;

    // Use UTC dates to avoid timezone issues
    const today = new Date();
    const birth = new Date(`${birthDate}T00:00:00.000Z`); // Force UTC interpretation

    if (Number.isNaN(birth.getTime())) return 0; // Handle invalid dates

    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age -= 1;
    }

    return Math.max(0, age);
  };

  const patientAge = calculateAge(formData.birthDate);

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          type="button"
          onClick={onCancel}
          className="p-2 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded-full"
          aria-label="Go back to patient list"
        >
          <ArrowLeftIcon className="w-5 h-5" />
        </button>
        <h2 className="text-xl font-semibold text-gray-900">Create New Patient</h2>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="flex-1 space-y-6">
        {/* Patient Information */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Patient Information</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <StyledLabel htmlFor="name">First Name *</StyledLabel>
              <input
                id="name"
                type="text"
                value={formData.name}
                onChange={handleInputChange('name')}
                required
                className={`appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm ${
                  errors.name
                    ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                    : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
                }`}
              />
              {errors.name && <div className="mt-1 text-sm text-red-600">{errors.name}</div>}
            </div>

            <div>
              <StyledLabel htmlFor="lastName">Last Name *</StyledLabel>
              <input
                id="lastName"
                type="text"
                value={formData.lastName}
                onChange={handleInputChange('lastName')}
                required
                className={`appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm ${
                  errors.lastName
                    ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                    : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
                }`}
              />
              {errors.lastName && <div className="mt-1 text-sm text-red-600">{errors.lastName}</div>}
            </div>

            <div>
              <StyledLabel htmlFor="birthDate">Birth Date</StyledLabel>
              <div className="relative">
                <DatePicker
                  id="birthDate"
                  selected={selectedDate}
                  onChange={handleDateChange}
                  dateFormat="yyyy-MM-dd"
                  placeholderText="Select birth date"
                  maxDate={new Date()}
                  showYearDropdown
                  showMonthDropdown
                  yearDropdownItemNumber={100}
                  scrollableYearDropdown
                  popperClassName="!z-[9999]"
                  popperPlacement="bottom-start"
                  className={`appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm ${
                    errors.birthDate
                      ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
                  }`}
                />
              </div>
              {errors.birthDate && <div className="mt-1 text-sm text-red-600">{errors.birthDate}</div>}
              {formData.birthDate && (
                <p className="mt-1 text-sm text-gray-500">
                  Age: {patientAge} year{patientAge !== 1 ? 's' : ''}
                </p>
              )}
            </div>

            <div>
              <StyledLabel htmlFor="personCode">Person Code</StyledLabel>
              <input
                id="personCode"
                type="text"
                value={formData.personCode}
                onChange={handleInputChange('personCode')}
                className={`appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm ${
                  errors.personCode
                    ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                    : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
                }`}
              />
              {errors.personCode && <div className="mt-1 text-sm text-red-600">{errors.personCode}</div>}
            </div>

            <div>
              <StyledLabel htmlFor="genderId">Gender *</StyledLabel>
              <select
                id="genderId"
                value={formData.genderId}
                onChange={handleInputChange('genderId')}
                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${
                  errors.genderId ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                }`}
                required
              >
                <option value={1}>Male</option>
                <option value={2}>Female</option>
                <option value={3}>Other</option>
              </select>
              {errors.genderId && <div className="mt-1 text-sm text-red-600">{errors.genderId}</div>}
            </div>
          </div>
        </div>

        {/* Spacer for calendar */}
        <div style={{ height: '300px' }} />

        {/* Form Actions */}
        <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading && <CommonAnimatedSpinner sm />}
            {isLoading ? 'Creating...' : 'Create Patient'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default PatientCreateForm;
